export function hexToRgb(hex) {
  const normalized = hex.replace('#', '');
  if (![3, 6, 8].includes(normalized.length)) return null;
  const parts = normalized.length === 3
    ? normalized.split('').map((c) => c + c)
    : normalized.match(/.{1,2}/g);
  if (!parts || parts.length < 3) return null;
  const [r, g, b] = parts.map((p) => parseInt(p, 16));
  if ([r, g, b].some((v) => Number.isNaN(v))) return null;
  return { r, g, b };
}

export function rgbToHex({ r, g, b }) {
  const toHex = (v) => v.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function rgbToHsl({ r, g, b }) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h;
  let s;
  const l = (max + min) / 2;
  if (max === min) {
    h = 0;
    s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  return { h: h * 360, s, l };
}

export function hslToRgb({ h, s, l }) {
  h /= 360;
  let r;
  let g;
  let b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

export function deriveAccentColor(primaryHex) {
  const rgb = hexToRgb(primaryHex);
  if (!rgb) return '#9DE94B';
  const hsl = rgbToHsl(rgb);
  // shift hue by ~150° for a lively but not harsh accent
  hsl.h = (hsl.h + 150) % 360;
  // keep saturation high and lightness moderate for UI accent
  hsl.s = Math.min(0.85, Math.max(0.45, hsl.s));
  hsl.l = Math.min(0.65, Math.max(0.45, hsl.l));
  return rgbToHex(hslToRgb(hsl));
}

export function adjustLightness(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const hsl = rgbToHsl(rgb);
  hsl.l = Math.min(0.95, Math.max(0.05, hsl.l + amount));
  return rgbToHex(hslToRgb(hsl));
}
